/**
 * タイムラインメモカード
 *
 * タイムラインに表示するメモカード
 * 編集・削除機能付き
 */

import { MessageSquare, Pencil, Trash2 } from 'lucide-react'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'

import { deleteTimelineNote, updateTimelineNote } from '@/api/services/overland'
import type { TimelineNote } from '@/api/types/overland'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { formatTimeJST } from '@/lib/activity-format-utils'
import { confirmDialog } from '@/lib/confirm-dialog'
import { cn } from '@/lib/utils'

type Props = {
  note: TimelineNote
  /** 編集成功時：更新後のメモを返す */
  onChanged?: (note: TimelineNote) => void
  /** 削除成功時：削除した noteId を返す */
  onDeleted?: (noteId: number) => void
  className?: string
}

export const TimelineNoteCard = ({ note, onChanged, onDeleted, className }: Props) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState(note.content)
  const { isSubmitting: isSaving, run } = useSubmitLock()

  const handleSave = useCallback(async () => {
    if (!editContent.trim()) return
    await run(async () => {
      try {
        const updated = await updateTimelineNote(note.id, { content: editContent.trim() })
        setIsEditing(false)
        onChanged?.(updated)
      } catch {
        toast.error('メモの更新に失敗しました')
      }
    })
  }, [note.id, editContent, onChanged, run])

  const handleDelete = useCallback(async () => {
    if (!(await confirmDialog({ title: 'このメモを削除しますか？', destructive: true }))) return
    try {
      await deleteTimelineNote(note.id)
      toast.success('メモを削除しました')
      onDeleted?.(note.id)
    } catch {
      toast.error('メモの削除に失敗しました')
    }
  }, [note.id, onDeleted])

  const handleCancel = useCallback(() => {
    setEditContent(note.content)
    setIsEditing(false)
  }, [note.content])

  return (
    <div
      className={cn(
        'rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 shadow-sm',
        'transition-all duration-300',
        className,
      )}
    >
      {/* ヘッダー */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
          <MessageSquare className="size-3.5" />
          <span>{formatTimeJST(note.timestamp)}</span>
        </div>
        {!isEditing && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="size-6"
              onClick={() => {
                setEditContent(note.content)
                setIsEditing(true)
              }}
            >
              <Pencil className="size-3 text-muted-foreground" />
            </Button>
            <Button variant="ghost" size="icon" className="size-6" onClick={handleDelete}>
              <Trash2 className="size-3 text-destructive" />
            </Button>
          </div>
        )}
      </div>

      {/* 本文 */}
      {isEditing ? (
        <div className="space-y-2">
          <Textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="min-h-[80px] text-sm"
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={handleCancel} disabled={isSaving}>
              キャンセル
            </Button>
            <Button size="sm" onClick={handleSave} disabled={isSaving || !editContent.trim()}>
              保存
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm whitespace-pre-wrap">{note.content}</p>
      )}
    </div>
  )
}
